import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { CheckCircle2, CircleAlert, Clock, PackageSearch, Truck, type LucideIcon } from 'lucide-react';
import type { EstadoSolicitacaoAbastecimento, FiltroFilaAbastecimento, OrdenacaoFilaAbastecimento, SituacaoFilaAbastecimento } from '@pieltec/contracts';
import { EntregaAbastecimentoDialog, TEXTO_CONFLITO, TEXTO_OFFLINE } from './entrega-abastecimento-dialog';
import { SolicitacaoAbastecimentoDetalhe } from './solicitacao-abastecimento-detalhe';
import { abastecimentoApi } from '../../lib/estoque/abastecimento-api';
import { lerEstadoDaUrl } from '../../lib/estoque/abastecimento-url';
import { entradaSeparacao, linhaDaFila, linhaSelecionada, type LinhaFila } from './abastecimento-linha';
import { criarCarregadorFila } from './carregador-fila';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { obterSessaoAtual } from '../../lib/session';
import { PedirMaterialDesktopDialog } from './pedir-material-desktop-dialog';
import { CampoSelect } from '../../components/ui/campo-select';

export { linhaDaFila, linhaSelecionada, type LinhaFila };

export interface ColunaFila {
  readonly situacao: SituacaoFilaAbastecimento;
  readonly titulo: string;
  readonly itens: readonly LinhaFila[];
}

const ROTULO: Record<EstadoSolicitacaoAbastecimento, string> = { SOLICITADA: 'A separar', PARCIALMENTE_SEPARADA: 'Em separação', SEPARADA: 'Separada', ENTREGUE: 'Entregue', CANCELADA: 'Cancelada' };
const ROTULO_SITUACAO: Record<SituacaoFilaAbastecimento, string> = { A_SEPARAR: 'A separar', EM_SEPARACAO: 'Em separação', ENTREGA_PARCIAL: 'Entrega parcial', ENTREGUE: 'Entregue' };
const ORDEM_COLUNAS: readonly SituacaoFilaAbastecimento[] = ['A_SEPARAR', 'EM_SEPARACAO', 'ENTREGA_PARCIAL', 'ENTREGUE'];
const ICONE_SITUACAO: Record<SituacaoFilaAbastecimento, { readonly Icone: LucideIcon; readonly classe: string }> = {
  A_SEPARAR: { Icone: Clock, classe: 'text-muted-foreground' },
  EM_SEPARACAO: { Icone: PackageSearch, classe: 'text-warning' },
  ENTREGA_PARCIAL: { Icone: Truck, classe: 'text-warning' },
  ENTREGUE: { Icone: CheckCircle2, classe: 'text-success' },
};
const TAMANHO_PAGINA = 20;
const LINHAS_SKELETON_POR_COLUNA = 2;
const ID_AVISO_OFFLINE = 'abastecimento-aviso-offline';

type FiltrosTela = { readonly situacao: SituacaoFilaAbastecimento | ''; readonly setor: string; readonly loteOuOp: string; readonly material: string; readonly de: string; readonly ate: string; readonly ordenacao: OrdenacaoFilaAbastecimento };
const FILTROS_INICIAIS: FiltrosTela = { situacao: '', setor: '', loteOuOp: '', material: '', de: '', ate: '', ordenacao: 'MAIS_ANTIGAS' };
const CAMPO = 'ml-2 min-h-11 rounded-md border border-border bg-background px-2';
const CAMPO_SELECT = 'ml-2 inline-flex min-h-11 w-52 align-middle';
const BOTAO = 'inline-flex min-h-11 items-center justify-center rounded-md border border-border px-4 font-semibold disabled:opacity-50';
const LINK = 'inline-flex min-h-11 items-center px-2 font-semibold text-primary underline';
const BOTAO_PRIMARIO = 'inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50';

function filtroApi(filtros: FiltrosTela): FiltroFilaAbastecimento {
  return { situacao: filtros.situacao || undefined, setor: filtros.setor || undefined, loteOuOp: filtros.loteOuOp || undefined, material: filtros.material || undefined, de: filtros.de || undefined, ate: filtros.ate || undefined, ordenacao: filtros.ordenacao };
}

/** Quatro colunas na ordem do contrato; preserva a ordem do servidor dentro de cada coluna. CANCELADA/null ficam fora. */
export function colunasDaFila(itens: readonly LinhaFila[]): readonly ColunaFila[] {
  return ORDEM_COLUNAS.map((situacao) => ({ situacao, titulo: ROTULO_SITUACAO[situacao], itens: itens.filter((item) => item.situacao === situacao) }));
}

function temPendencia(item: LinhaFila): boolean {
  return /[1-9]/.test(item.pendente);
}

export type EstadoFila = 'carregando' | 'erro' | 'pronto';

export function podePedirMaterialNoDesktop(permissoes: readonly string[]): boolean {
  return permissoes.includes('estoque:solicitar');
}

export function CabecalhoAbastecimento({ podeSolicitar, onPedir }: { readonly podeSolicitar: boolean; readonly onPedir: () => void }): ReactElement {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="space-y-1"><h1 className="text-xl font-semibold">Abastecimento de materiais</h1><p className="text-sm text-muted-foreground">Fila física do almoxarifado. Separação e entrega são fatos distintos.</p></div>
      {podeSolicitar && <button type="button" className={BOTAO_PRIMARIO} onClick={onPedir}>Pedir material</button>}
    </header>
  );
}

export interface AbastecimentoFilaConteudoProps {
  readonly estado: EstadoFila;
  readonly itens: readonly LinhaFila[];
  readonly filtros: ReactNode;
  readonly total: number;
  readonly pagina: number;
  readonly tamanhoPagina?: number;
  readonly falhaAtualizacao?: boolean;
  readonly offline?: boolean;
  readonly onHistorico?: (item: LinhaFila) => void;
  readonly onTentarNovamente?: () => void;
  readonly onSeparar?: (item: LinhaFila) => void;
  readonly onEntregar?: (item: LinhaFila) => void;
  readonly onPagina?: (pagina: number) => void;
}

function MensagemFalha({ onTentarNovamente }: { readonly onTentarNovamente?: () => void }): ReactElement {
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-danger p-4 text-sm text-danger">
      <span className="inline-flex items-center gap-2"><CircleAlert className="size-4" aria-hidden="true" />Não foi possível carregar os dados. Verifique a conexão e tente novamente.</span>
      <button type="button" className={BOTAO} onClick={onTentarNovamente}>Tentar novamente</button>
    </div>
  );
}

function CartaoSolicitacao({ item, offline, onSeparar, onEntregar, onHistorico }: { readonly item: LinhaFila; readonly offline: boolean; readonly onSeparar?: (item: LinhaFila) => void; readonly onEntregar?: (item: LinhaFila) => void; readonly onHistorico?: (item: LinhaFila) => void }): ReactElement {
  const encerrada = item.estado === 'ENTREGUE' || item.estado === 'CANCELADA';
  const aviso = offline ? ID_AVISO_OFFLINE : undefined;
  return (
    <article className="min-w-0 space-y-2 rounded-md border border-border bg-card p-4 text-card-foreground">
      <p className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1 font-semibold" data-slot="situacao-cartao">
          <SituacaoRotulo situacao={item.situacao} estado={item.estado} />
        </span>
        <time className="tabular-nums text-muted-foreground">{item.criadaEm}</time>
      </p>
      <p className="min-w-0 truncate font-semibold" title={item.material}>{item.material}</p>
      <p className="min-w-0 truncate text-sm" title={item.destino}>{item.destino}</p>
      <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-md border border-border px-2 py-1 font-semibold" data-slot="origem-cartao">{item.origemTexto}</span>
        <span>{item.natureza}</span>
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-xs font-semibold">Solicitado</dt>
        <dd className="text-right tabular-nums font-semibold">{item.solicitadoTexto}</dd>
        <dt className="text-xs font-semibold">Separado</dt>
        <dd className="text-right tabular-nums">{item.separadoTexto}</dd>
        <dt className="text-xs font-semibold">Entregue</dt>
        <dd className="text-right tabular-nums">{item.entregueTexto}</dd>
        <dt className="text-xs font-semibold">Pendente</dt>
        <dd className="text-right tabular-nums font-semibold">{item.pendenteTexto}</dd>
      </dl>
      <p className="min-w-0 truncate text-xs text-muted-foreground">{item.solicitante}</p>
      <p className="text-xs text-muted-foreground">Separar não movimenta estoque.</p>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={LINK} onClick={() => onHistorico?.(item)}>Ver histórico</button>
        <button type="button" className={BOTAO} disabled={encerrada || offline || !onSeparar} aria-describedby={aviso} onClick={() => onSeparar?.(item)}>Registrar separação</button>
        <button type="button" className={BOTAO_PRIMARIO} disabled={encerrada || offline || !onEntregar} aria-describedby={aviso} onClick={() => onEntregar?.(item)}>Confirmar entrega física</button>
      </div>
    </article>
  );
}

function SituacaoRotulo({ situacao, estado }: { readonly situacao: SituacaoFilaAbastecimento | null; readonly estado: EstadoSolicitacaoAbastecimento }): ReactElement {
  if (!situacao) return <><CircleAlert className="size-4 text-muted-foreground" aria-hidden="true" />{ROTULO[estado]}</>;
  const { Icone, classe } = ICONE_SITUACAO[situacao];
  return <><Icone className={`size-4 ${classe}`} aria-hidden="true" />{ROTULO_SITUACAO[situacao]}</>;
}

/** Fila do almoxarifado em quatro colunas (UI-47, UI-48, UI-49, UI-50, UI-53). Presentacional: testável por renderToStaticMarkup. */
export function AbastecimentoFilaConteudo({ estado, itens, filtros, total, pagina, tamanhoPagina = TAMANHO_PAGINA, falhaAtualizacao = false, offline = false, onTentarNovamente, onSeparar, onEntregar, onHistorico, onPagina }: AbastecimentoFilaConteudoProps): ReactElement {
  const colunas = colunasDaFila(itens);
  const pendencias = itens.filter(temPendencia).length;
  let corpo: ReactElement;
  if (estado === 'erro') {
    corpo = <MensagemFalha onTentarNovamente={onTentarNovamente} />;
  } else if (estado === 'carregando') {
    corpo = (
      <div aria-busy="true" className="grid gap-4 lg:grid-cols-4">
        {colunas.map((coluna) => (
          <section key={coluna.situacao} aria-label={coluna.titulo} className="min-w-0 space-y-2">
            <h2 className="text-xs font-semibold">{coluna.titulo}</h2>
            {Array.from({ length: LINHAS_SKELETON_POR_COLUNA }, (_, indice) => <Skeleton key={indice} className="h-24 w-full" />)}
          </section>
        ))}
      </div>
    );
  } else if (itens.length === 0) {
    corpo = (
      <div className="space-y-2 rounded-md border border-dashed border-border p-8 text-center">
        <p className="font-semibold">Nenhuma solicitação de abastecimento para estes filtros.</p>
        <p className="text-sm text-muted-foreground">Ajuste os filtros ou aguarde uma liberação de lote ou solicitação do setor.</p>
      </div>
    );
  } else {
    corpo = (
      <>
        {falhaAtualizacao && <MensagemFalha onTentarNovamente={onTentarNovamente} />}
        {offline && <p id={ID_AVISO_OFFLINE} role="status" className="text-sm text-warning">{TEXTO_OFFLINE}</p>}
        <p className="text-sm">
          <span className="text-[28px] font-semibold leading-tight tabular-nums" data-slot="pendencias">{pendencias}</span>{' '}
          {pendencias === 1 ? 'solicitação com saldo pendente nesta página' : 'solicitações com saldo pendente nesta página'}
        </p>
        <div className="grid gap-4 lg:grid-cols-4">
          {colunas.map((coluna) => (
            <section key={coluna.situacao} aria-label={coluna.titulo} className="min-w-0 space-y-2">
              <h2 className="flex items-center justify-between text-xs font-semibold"><span>{coluna.titulo}</span><span className="tabular-nums text-muted-foreground">{coluna.itens.length}</span></h2>
              {coluna.itens.map((item) => <CartaoSolicitacao key={item.chave} item={item} offline={offline} onSeparar={onSeparar} onEntregar={onEntregar} onHistorico={onHistorico} />)}
            </section>
          ))}
        </div>
        <div className="flex items-center justify-between gap-4">
          <button type="button" className={BOTAO} disabled={pagina <= 1} onClick={() => onPagina?.(pagina - 1)}>Anterior</button>
          <span className="text-sm tabular-nums">Página {pagina} · {total} solicitações</span>
          <button type="button" className={BOTAO} disabled={pagina * tamanhoPagina >= total} onClick={() => onPagina?.(pagina + 1)}>Próxima</button>
        </div>
      </>
    );
  }
  return (
    <div className="space-y-4">
      {filtros}
      {corpo}
    </div>
  );
}

function BarraFiltros({ estado, filtros, onEstado, onFiltro, onAtualizar }: { readonly estado: EstadoSolicitacaoAbastecimento | ''; readonly filtros: FiltrosTela; readonly onEstado: (estado: EstadoSolicitacaoAbastecimento | '') => void; readonly onFiltro: <K extends keyof FiltrosTela>(chave: K, valor: FiltrosTela[K]) => void; readonly onAtualizar: () => void }): ReactElement {
  return (
    <div role="search" aria-label="Filtros da fila" className="flex flex-wrap items-end gap-4 rounded-md border border-border bg-card p-4">
      <label className="text-sm font-semibold">Estado<CampoSelect aria-label="Estado" value={estado} onValueChange={(valor) => onEstado(valor as EstadoSolicitacaoAbastecimento | '')} options={[{ value: '', label: 'Todos' }, ...Object.entries(ROTULO).map(([valor, rotulo]) => ({ value: valor, label: rotulo }))]} className={CAMPO_SELECT} /></label>
      <label className="text-sm font-semibold">Situação<CampoSelect aria-label="Situação" value={filtros.situacao} onValueChange={(valor) => onFiltro('situacao', valor as SituacaoFilaAbastecimento | '')} options={[{ value: '', label: 'Todas' }, ...Object.entries(ROTULO_SITUACAO).map(([valor, rotulo]) => ({ value: valor, label: rotulo }))]} className={CAMPO_SELECT} /></label>
      <label className="text-sm font-semibold">Setor<input value={filtros.setor} onChange={(e) => onFiltro('setor', e.target.value)} maxLength={80} placeholder="Código da célula" className={CAMPO} /></label>
      <label className="text-sm font-semibold">OP ou lote<input value={filtros.loteOuOp} onChange={(e) => onFiltro('loteOuOp', e.target.value)} maxLength={80} placeholder="Número da OP ou lote" className={CAMPO} /></label>
      <label className="text-sm font-semibold">Material<input value={filtros.material} onChange={(e) => onFiltro('material', e.target.value)} maxLength={80} placeholder="Código ou nome" className={CAMPO} /></label>
      <label className="text-sm font-semibold">De<input type="date" value={filtros.de} onChange={(e) => onFiltro('de', e.target.value)} className={CAMPO} /></label>
      <label className="text-sm font-semibold">Até<input type="date" value={filtros.ate} onChange={(e) => onFiltro('ate', e.target.value)} className={CAMPO} /></label>
      <label className="text-sm font-semibold">Ordenação<CampoSelect aria-label="Ordenação" value={filtros.ordenacao} onValueChange={(valor) => onFiltro('ordenacao', valor as OrdenacaoFilaAbastecimento)} options={[{ value: 'MAIS_ANTIGAS', label: 'Mais antigas primeiro' }, { value: 'MAIS_RECENTES', label: 'Mais recentes primeiro' }]} className={CAMPO_SELECT} /></label>
      <button type="button" onClick={onAtualizar} className={BOTAO}>Atualizar</button>
    </div>
  );
}

export const TEXTO_LINHA_FORA_DA_FILA = 'Esta solicitação não está mais na fila com os filtros atuais.';

/**
 * Estado "recarregar" de uma ação cujo cartão saiu da fila (filtro, recarga ou outra pessoa): nunca troca a ação
 * para outra solicitação ou outro item.
 */
export function LinhaForaDaFilaCorpo({ titulo, descricao, onRecarregar, onFechar }: { readonly titulo?: ReactNode; readonly descricao?: ReactNode; readonly onRecarregar?: () => void; readonly onFechar?: () => void }): ReactElement {
  return (
    <>
      <FormDialogHeader>
        {titulo ?? <h2 className="text-xl font-semibold">Solicitação indisponível</h2>}
        {descricao ?? <p className="text-sm text-muted-foreground">{TEXTO_LINHA_FORA_DA_FILA}</p>}
      </FormDialogHeader>
      <FormDialogBody>
        <div role="alert" className="space-y-2 rounded-md border border-danger p-4 text-sm text-danger">
          <p className="inline-flex items-start gap-2"><CircleAlert className="size-4 shrink-0" aria-hidden="true" /><span className="min-w-0 break-words">{TEXTO_CONFLITO}</span></p>
        </div>
      </FormDialogBody>
      <FormDialogFooter>
        <button type="button" className={BOTAO} onClick={onFechar}>Voltar sem confirmar</button>
        <button type="button" className={BOTAO} onClick={onRecarregar}>Recarregar situação atual</button>
      </FormDialogFooter>
    </>
  );
}

function LinhaForaDaFilaDialog({ onRecarregar, onFechar }: { readonly onRecarregar: () => void; readonly onFechar: () => void }): ReactElement {
  return (
    <FormDialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <FormDialogContent>
        <LinhaForaDaFilaCorpo
          titulo={<FormDialogTitle className="text-xl font-semibold">Solicitação indisponível</FormDialogTitle>}
          descricao={<FormDialogDescription className="text-sm text-muted-foreground">{TEXTO_LINHA_FORA_DA_FILA}</FormDialogDescription>}
          onRecarregar={onRecarregar} onFechar={onFechar}
        />
      </FormDialogContent>
    </FormDialog>
  );
}

/** Estado de conexão do navegador; escrita só é oferecida online (nunca sucesso local). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const ligar = (): void => setOnline(true);
    const desligar = (): void => setOnline(false);
    window.addEventListener('online', ligar);
    window.addEventListener('offline', desligar);
    return () => { window.removeEventListener('online', ligar); window.removeEventListener('offline', desligar); };
  }, []);
  return online;
}

export function AbastecimentoPage(): ReactElement {
  const [estado, setEstado] = useState<EstadoSolicitacaoAbastecimento | ''>(() => lerEstadoDaUrl(typeof window !== 'undefined' ? window.location.search : ''));
  const [filtros, setFiltros] = useState<FiltrosTela>(FILTROS_INICIAIS);
  const [pagina, setPagina] = useState(1);
  const [itens, setItens] = useState<readonly LinhaFila[] | null>(null);
  const [total, setTotal] = useState(0);
  const [falhou, setFalhou] = useState(false);
  // Ações guardam a CHAVE do cartão clicado (solicitação + item) e resolvem a linha na fila atual só por ela.
  const [entregaChave, setEntregaChave] = useState<string | null>(null);
  const [historico, setHistorico] = useState<{ readonly id: string; readonly itemId: string } | null>(null);
  const online = useOnline();
  const [separandoChave, setSeparandoChave] = useState<string | null>(null);
  const [localizacaoSeparacao, setLocalizacaoSeparacao] = useState('');
  const [quantidadeSeparacao, setQuantidadeSeparacao] = useState('');
  const [erroSeparacao, setErroSeparacao] = useState<string | null>(null);
  const [enviandoSeparacao, setEnviandoSeparacao] = useState(false);
  const [podeSolicitar, setPodeSolicitar] = useState(false);
  const [pedirAberto, setPedirAberto] = useState(false);
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => { if (vivo) setPodeSolicitar(sessao.autenticado && podePedirMaterialNoDesktop(sessao.usuario.permissions)); });
    return () => { vivo = false; };
  }, []);
  // Última leitura vence: aborta a anterior e só a resposta mais recente SUBSTITUI a lista (UAT-16).
  const carregador = useMemo(() => criarCarregadorFila({
    listar: (filtro, opcoes) => abastecimentoApi.listarFila(filtro, opcoes),
    aplicar: (fila) => { setItens(fila.itens); setTotal(fila.total); },
    falhar: () => setFalhou(true),
  }), []);
  useEffect(() => () => carregador.cancelar(), [carregador]);

  const carregar = useCallback(async (): Promise<void> => {
    setFalhou(false);
    await carregador.carregar({ pagina, tamanhoPagina: TAMANHO_PAGINA, estado: estado || undefined, ...filtroApi(filtros) });
  }, [carregador, estado, filtros, pagina]);
  useEffect(() => { void carregar(); }, [carregar]);

  const entrega = linhaSelecionada(itens ?? [], entregaChave);
  const separando = linhaSelecionada(itens ?? [], separandoChave);
  const fecharSeparacao = (): void => { setSeparandoChave(null); setLocalizacaoSeparacao(''); setQuantidadeSeparacao(''); setErroSeparacao(null); };

  const mudarFiltro = <K extends keyof FiltrosTela>(chave: K, valor: FiltrosTela[K]): void => { setPagina(1); setFiltros((atual) => ({ ...atual, [chave]: valor })); };

  const separar = async (): Promise<void> => {
    if (!online) { setErroSeparacao(TEXTO_OFFLINE); return; }
    if (!separando) { setErroSeparacao(TEXTO_CONFLITO); return; }
    if (!localizacaoSeparacao.trim() || !(Number(quantidadeSeparacao.replace(',', '.')) > 0)) { setErroSeparacao('Selecione a localização e informe uma quantidade positiva.'); return; }
    setEnviandoSeparacao(true); setErroSeparacao(null);
    try {
      // A separação mira o item do cartão: numa solicitação com vários materiais o servidor recusa a ambiguidade.
      await abastecimentoApi.separar(separando.id, entradaSeparacao(separando, localizacaoSeparacao, quantidadeSeparacao, globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`));
      fecharSeparacao(); await carregar();
    } catch (causa) { setErroSeparacao(causa instanceof Error ? causa.message : 'Não foi possível registrar a separação.'); }
    finally { setEnviandoSeparacao(false); }
  };

  const filtrosNode = <BarraFiltros estado={estado} filtros={filtros} onEstado={(valor) => { setPagina(1); setEstado(valor); }} onFiltro={mudarFiltro} onAtualizar={() => void carregar()} />;
  const estadoFila: EstadoFila = itens === null ? (falhou ? 'erro' : 'carregando') : 'pronto';

  return (
    <main className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
      <CabecalhoAbastecimento podeSolicitar={podeSolicitar} onPedir={() => setPedirAberto(true)} />
      <AbastecimentoFilaConteudo
        estado={estadoFila} itens={itens ?? []} filtros={filtrosNode} total={total} pagina={pagina} falhaAtualizacao={falhou} offline={!online}
        onTentarNovamente={() => void carregar()} onPagina={setPagina}
        onSeparar={(item) => { setSeparandoChave(item.chave); setErroSeparacao(null); }} onEntregar={(item) => setEntregaChave(item.chave)} onHistorico={(item) => setHistorico({ id: item.id, itemId: item.itemId })}
      />
      {/* key = chave do cartão: outra linha remonta o diálogo e descarta quantidade e chave de idempotência da anterior. */}
      {entrega && <EntregaAbastecimentoDialog key={entrega.chave} aberto onAbertoChange={(aberto) => !aberto && setEntregaChave(null)} linha={entrega} offline={!online} onEntregue={() => void carregar()} onRecarregar={carregar} />}
      {entregaChave !== null && !entrega && itens !== null && <LinhaForaDaFilaDialog onRecarregar={() => void carregar()} onFechar={() => setEntregaChave(null)} />}
      {historico && <SolicitacaoAbastecimentoDetalhe key={`${historico.id}:${historico.itemId}`} aberto onAbertoChange={(aberto) => !aberto && setHistorico(null)} solicitacaoId={historico.id} itemId={historico.itemId} />}
      {separandoChave !== null && !separando && itens !== null && <LinhaForaDaFilaDialog onRecarregar={() => void carregar()} onFechar={fecharSeparacao} />}
      {separando && (
        <FormDialog key={separando.chave} open onOpenChange={(aberto) => !aberto && fecharSeparacao()}>
          <FormDialogContent>
            <FormDialogHeader><FormDialogTitle>Registrar separação</FormDialogTitle></FormDialogHeader>
            <FormDialogBody className="space-y-4">
              <p className="text-sm">A separação prepara {separando.material} para {separando.destino}; ela não move estoque nem confirma entrega.</p>
              <div className="block text-sm font-semibold">
                <label htmlFor="separacao-localizacao">Localização de separação</label>
                {/* WR-03: seleção de Localizacao pelo nome; o id segue internamente. */}
                <LocalizacaoSelect id="separacao-localizacao" rotulo="Localização de separação" value={localizacaoSeparacao} onChange={setLocalizacaoSeparacao} className="mt-1 min-h-11 w-full" />
              </div>
              <label className="block text-sm font-semibold">Quantidade separada<input inputMode="decimal" value={quantidadeSeparacao} onChange={(e) => setQuantidadeSeparacao(e.target.value)} className="mt-1 min-h-11 w-full rounded-md border border-border bg-background p-2" /></label>
              {erroSeparacao && <p role="alert" className="text-sm text-danger">{erroSeparacao}</p>}
              {!online && <p role="status" className="text-sm text-warning">{TEXTO_OFFLINE}</p>}
            </FormDialogBody>
            <FormDialogFooter>
              <button type="button" className={BOTAO} onClick={fecharSeparacao} disabled={enviandoSeparacao}>Cancelar</button>
              <button type="button" className={BOTAO} onClick={() => void separar()} disabled={enviandoSeparacao || !online}>{enviandoSeparacao ? 'Registrando…' : 'Confirmar separação'}</button>
            </FormDialogFooter>
          </FormDialogContent>
        </FormDialog>
      )}
      <PedirMaterialDesktopDialog aberto={pedirAberto} onAbertoChange={setPedirAberto} onSolicitado={() => void carregar()} />
    </main>
  );
}
